import {
  PriceDTO,
  ProductDTO,
  ProductDetailDTO,
  ProductSubType,
  UnlistedPriceDTO,
  SubscriptionDTO,
  UpgradeType,
  SwapType,
  PriceSelection,
  Role,
} from "@campgladiator/cg-common.types.types";
import { addDaysToISOstringDate } from "@campgladiator/cg-common.utility.dates";
import { centsToUSD } from "@campgladiator/cg-common.utility.numbers";
import moment from "moment";

export const upgradeEligibleSubscriptionTypes: Array<
  SubscriptionDTO["productType"]
> = ["BOOTCAMP"];

export const upgradeOrCancelEligibleSubscriptionStatuses: Array<
  SubscriptionDTO["status"]
> = ["ACTIVE", "TRIAL"];

export type IsProductUpgradeEligibleArgs = {
  product: ProductDTO;
  initialTermLength: number;
  upgradeType: UpgradeType;
  selectedProductId: string;
  selectedProductSubType: ProductSubType;
  inContract: boolean;
  swapType?: SwapType;
  priceSelection?: PriceSelection;
};

export const isProductCreationEligible = ({
  productDetail,
  userSubscriptions,
}: {
  productDetail: ProductDetailDTO;
  userSubscriptions: SubscriptionDTO[];
}) => {
  const isNotDeleted = !productDetail.deleted;
  const isNotAlreadySubscribed = !userSubscriptions
    ?.filter((c) => c.status !== "CANCELED")
    ?.map((c) => c.productType)
    ?.includes(productDetail.type);
  return isNotDeleted && isNotAlreadySubscribed;
};

export const isProductUpgradeEligible = ({
  product,
  initialTermLength,
  upgradeType,
  selectedProductId,
  selectedProductSubType,
  inContract,
  swapType,
  priceSelection,
}: IsProductUpgradeEligibleArgs) => {
  const isUpgradeType = upgradeType === "UPGRADE";
  const isRestartSwapType = swapType === "RESTART";
  const isActive = product.active;
  const isNotDeleted = !product.deleted;
  const meetsUpgradeTermLengthCriteria = isUpgradeType
    ? (!inContract && product.termLength >= 1) ||
      (inContract && product.termLength >= initialTermLength)
    : true;

  const meetsSwapCriteria =
    upgradeType === "SWAP" &&
    product.stripeProductId !== selectedProductId &&
    product.subType !== selectedProductSubType;

  const meetsSwapRestartCriteria =
    upgradeType === "SWAP" &&
    swapType === "RESTART" &&
    ((inContract &&
      product.termLength >= initialTermLength &&
      product.termLength > 1) ||
      (!inContract && product.termLength >= 1));

  return (
    meetsUpgradeTermLengthCriteria &&
    ((meetsSwapCriteria && (meetsSwapRestartCriteria || !isRestartSwapType)) ||
      isUpgradeType) &&
    isActive &&
    isNotDeleted
  );
};

export const findUpgradeEligibleProducts = ({
  selectedProduct,
  selectedProductTermLength,
  upgradeType,
  selectedProductId,
  selectedProductSubType,
  inContract,
  swapType,
  priceSelection,
}: {
  selectedProduct: ProductDetailDTO;
  selectedProductTermLength: number;
  upgradeType: UpgradeType;
  selectedProductId: string;
  selectedProductSubType: ProductSubType;
  inContract: boolean;
  swapType?: SwapType;
  priceSelection?: PriceSelection;
}) =>
  !selectedProduct.deleted
    ? selectedProduct.products.filter((product: ProductDTO) =>
        isProductUpgradeEligible({
          product,
          initialTermLength: selectedProductTermLength,
          upgradeType,
          selectedProductId,
          selectedProductSubType,
          inContract,
          swapType,
          priceSelection,
        })
      )
    : [];

export const isProductUpgradable = ({
  status,
  productType,
  archivedAt,
}: {
  status: SubscriptionDTO["status"];
  productType: SubscriptionDTO["productType"];
  archivedAt: SubscriptionDTO["archivedAt"];
}) =>
  upgradeEligibleSubscriptionTypes.includes(productType) &&
  upgradeOrCancelEligibleSubscriptionStatuses.includes(status) &&
  !archivedAt;

export const getUpgradeableProducts = async ({
  subscription,
  productDetails,
  upgradeType,
  unlistedPrices,
  swapType,
  priceSelection,
  role,
  userSubscriptions,
}: {
  priceSelection?: PriceSelection;
  productDetails?: ProductDetailDTO[];
  subscription?: SubscriptionDTO;
  swapType?: SwapType;
  unlistedPrices?: UnlistedPriceDTO[];
  upgradeType?: UpgradeType;
  role?: Role;
  userSubscriptions?: SubscriptionDTO[];
}) => {
  let selectedProductTermLength: number = 0;
  let selectedProductSubType: ProductSubType = "IN_PERSON";

  let productId: string = "";
  let productName: string = "";
  let productPrice: string = "";
  let inContract: boolean = false;

  if (subscription) {
    productId = subscription.productId ?? "";
    productName = subscription.productName ?? "";
    productPrice = centsToUSD(subscription.productPrice ?? 0) ?? "";
    inContract = subscription.contractStatistics.inContract ?? false;
  }

  const selectedProduct = productDetails?.find(({ products }) =>
    products.find((product) => {
      selectedProductTermLength = product.termLength;
      selectedProductSubType = product.subType;
      return product.stripeProductId === productId;
    })
  );

  let upgradeableProducts: ProductDTO[] = [];

  if (upgradeType === "UPGRADE")
    upgradeableProducts = selectedProduct
      ? findUpgradeEligibleProducts({
          selectedProduct,
          selectedProductTermLength,
          upgradeType,
          selectedProductId: productId,
          selectedProductSubType,
          inContract,
          swapType,
        })
      : [];
  else if (upgradeType === "SWAP") {
    if (productDetails) {
      productDetails.forEach((products) => {
        upgradeableProducts.push(
          ...findUpgradeEligibleProducts({
            selectedProduct: products,
            selectedProductTermLength,
            upgradeType,
            selectedProductId: productId,
            selectedProductSubType,
            inContract,
            swapType,
            priceSelection,
          })
        );
      });
    }
  } else if (!upgradeType) {
    upgradeableProducts =
      productDetails
        ?.filter((productDetail) =>
          isProductCreationEligible({
            productDetail,
            userSubscriptions: userSubscriptions ?? [],
          })
        )
        ?.map((products) => products.products)
        ?.flat() ?? [];
  }

  const getListedOrUnlistedPrices = (product: ProductDTO) =>
    unlistedPrices?.length
      ? (unlistedPrices?.filter(
          (price) => price.stripeProductId === product.stripeProductId
        ) as PriceDTO[])
      : product.prices;

  const upgradeableProductsWithRecommitPrice = upgradeableProducts
    ?.filter(
      (product) =>
        subscription?.termLength === 1 ||
        !(
          (role === "TRAINER" || role === "ADMIN") &&
          product.prices.some(
            (price) => price.type === "ONE_TIME" && price.termLength === 3
          )
        )
    )
    .map((product) => {
      const productOverriden = {
        ...product,
        prices: getListedOrUnlistedPrices(product),
      };
      return productOverriden;
    });

  const sortedUpgradeableProductsWithRecommitPrice = !!upgradeType
    ? upgradeableProductsWithRecommitPrice?.sort(
        (a, b) => b.termLength - a.termLength
      )
    : upgradeableProductsWithRecommitPrice?.sort((a, b) =>
        a.stripeProductName.localeCompare(b.stripeProductName)
      );

  return {
    upgradeableProducts: sortedUpgradeableProductsWithRecommitPrice,
    productName,
    productPrice,
  };
};

export const isMembershipCancelableOnline = (subscription: SubscriptionDTO) =>
  isMembershipCancelableByCustomerSupport(subscription) &&
  ((subscription.termLength === 1 && subscription.status !== "TRIAL") ||
    (!!subscription?.trialPeriodDays &&
      subscription.trialPeriodDays > 0 &&
      subscription.status === "TRIAL"));

export const isMembershipCancelableByCustomerSupport = (
  subscription: SubscriptionDTO
) =>
  upgradeOrCancelEligibleSubscriptionStatuses.includes(subscription.status) &&
  !isSubscriptionAlreadyCancelled(subscription);

export const isSubscriptionAlreadyCancelled = (subscription: SubscriptionDTO) =>
  !!subscription.archivedAt || subscription.status === "CANCELED";

const isSubscriptionActive = ({
  status,
}: {
  status: SubscriptionDTO["status"];
}) => upgradeOrCancelEligibleSubscriptionStatuses.includes(status);

export const hasActiveSubscriptions = (subscriptions: SubscriptionDTO[]) =>
  !!subscriptions.some((sub) => isSubscriptionActive(sub));

export const getCancelDate = (subscription: SubscriptionDTO) => {
  const currentDate = moment();
  const isTrial = subscription.status === "TRIAL";
  const cancelAtDateForInContracts = moment(subscription.periodEndDate)
    .add(subscription.contractStatistics.paymentsRemaining, "months")
    .add(-1, "days")
    .utcOffset(0)
    .toISOString();

  const cancelAtDateForTrials =
    isTrial &&
    addDaysToISOstringDate(
      subscription.periodStartDate ?? "",
      subscription.trialPeriodDays ?? 0
    ).toISOString();

  const cancelAtDateForOutContracts = moment(subscription?.periodEndDate!)
    .add(-1, "days")
    .utcOffset(0)
    .toISOString();

  const cancelDate = moment(
    isTrial
      ? cancelAtDateForTrials || ""
      : subscription.contractStatistics.inContract
      ? cancelAtDateForInContracts
      : cancelAtDateForOutContracts
  );

  return cancelDate.isBefore(currentDate)
    ? currentDate.add(1, "day").toISOString()
    : cancelDate.toISOString();
};
